import { gradeNumber } from './gradeYear';

/**
 * Reach / Target / Safety balance helpers for the college-list ring. Pure and
 * unit-tested. "reach" folds SUPER_REACH into REACH to match the Launchpad's
 * existing bands, so the ring summarizes exactly the categorization already on
 * the page (it can't claim a balance the list doesn't show).
 */

/** The three bands, in display order, with the ring/legend colors. */
export const BALANCE_BANDS = [
  { key: 'reach', label: 'Reach', color: '#C2410C' },   // orange-700
  { key: 'target', label: 'Target', color: '#1A4D2E' }, // brand green
  { key: 'safety', label: 'Safety', color: '#0369A1' }, // sky-700
];

/**
 * The category to bucket a college into for the balance ring: the student's
 * PERSONALIZED fit first (from a computed fit analysis, surfaced either nested
 * as `fit_analysis.fit_category` or top-level `fit_category` by the enriched
 * endpoint), then the population-level `soft_fit_category`. Null when neither.
 */
export function collegeFitCategory(college) {
  return college?.fit_analysis?.fit_category
    || college?.fit_category
    || college?.soft_fit_category
    || null;
}

/**
 * True when a college's band is only an admit-rate ESTIMATE — i.e. it has no
 * personalized fit (so the ring can honestly say how many are estimated).
 */
export function isEstimatedFit(college) {
  return !(college?.fit_analysis?.fit_category || college?.fit_category);
}

/**
 * Donut segments for the ring: each band with its count and fraction of the
 * total. Empty bands keep a zero fraction (so the legend can still list them).
 * @param {{reach?:number,target?:number,safety?:number}} counts
 */
export function balanceSegments({ reach = 0, target = 0, safety = 0 } = {}) {
  const counts = { reach, target, safety };
  const total = reach + target + safety;
  return BALANCE_BANDS.map((b) => ({
    ...b,
    count: counts[b.key],
    fraction: total > 0 ? counts[b.key] / total : 0,
  }));
}

/**
 * A human verdict on list balance — checks the classic applicant mistakes in
 * priority order (no safety, top-heavy, no targets) before declaring it healthy.
 * @returns {{tone: 'good'|'warn'|'info', headline: string, detail: string}}
 */
export function balanceVerdict({ reach = 0, target = 0, safety = 0 } = {}) {
  const total = reach + target + safety;
  if (total === 0) {
    return { tone: 'info', headline: 'No colleges yet', detail: 'Add a few schools to see your reach/target/safety balance.' };
  }
  if (safety === 0) {
    return { tone: 'warn', headline: 'No safety schools', detail: 'Add at least one safety you’d genuinely be happy to attend.' };
  }
  if (reach > target + safety) {
    return { tone: 'warn', headline: 'Top-heavy list', detail: `${reach} reaches vs ${target + safety} target/safety — add a couple of targets or safeties.` };
  }
  if (target === 0) {
    return { tone: 'warn', headline: 'No target schools', detail: 'A strong list leans on targets — add a few where you’re a likely admit.' };
  }
  if (reach > 0 && target > 0 && safety > 0) {
    return { tone: 'good', headline: 'Nicely balanced', detail: 'You’ve got reaches, targets and safeties covered.' };
  }
  return { tone: 'info', headline: 'Building your list', detail: 'Aim for a spread of reaches, targets and safeties.' };
}

/**
 * How long a list should be, from the "How many colleges to apply to" paper:
 * "10-15 schools for most students", and "past ~15 the essays dilute".
 * Founder, 2026-10-03 (#3041): a 1-1-1 list has a balanced SHAPE but is too
 * short, so the verdict carries length as a second dimension beside shape.
 * The two Python copies (season_moments.py, stratia_connector/list_balance.py)
 * hold the same numbers, pinned by tests/fixtures/list_balance_cases.json.
 */
export const LIST_LENGTH = { min: 10, max: 15 };

/** SUPER_REACH and REACH fold to 'reach'; an unknown band is null
 *  (season_moments.fold_band). */
export function foldBand(raw) {
  const v = String(raw || '').trim().toUpperCase();
  if (v === 'SUPER_REACH' || v === 'REACH') return 'reach';
  if (v === 'TARGET') return 'target';
  if (v === 'SAFETY') return 'safety';
  return null;
}

/** Reach / target / safety counts over list rows (season_moments.balance_counts). */
export function balanceCounts(colleges) {
  const counts = { reach: 0, target: 0, safety: 0 };
  for (const c of colleges || []) {
    const band = foldBand(collegeFitCategory(c));
    if (band) counts[band] += 1;
  }
  return counts;
}

/**
 * A senior's list is locked (season_moments._list_locked, the Season moment
 * "List locked"): a balanced shape, at least LIST_LENGTH.min schools (#3094),
 * and every school read in full. The year plan's "Lock your list" ticks on
 * this, never on a non-empty list (#3734: six schools read "done" beside the
 * moment's "6 of 10").
 */
export function isListLocked(colleges) {
  const rows = (colleges || []).filter((c) => c && typeof c === 'object');
  if (rows.length < LIST_LENGTH.min) return false;
  if (balanceVerdict(balanceCounts(rows)).tone !== 'good') return false;
  return rows.every((c) => !isEstimatedFit(c));
}

/**
 * True for a student still building a list (11th grade or earlier): the
 * length line reads as guidance, not a warning. Unknown grade is NOT still
 * building, so a senior with a blank grade still hears the plain verdict.
 */
export function isStillBuilding(grade) {
  const n = gradeNumber(grade);
  return n !== null && n < 12;
}

/**
 * The length half of the verdict. Null for an empty list (the shape verdict
 * already says "No colleges yet").
 * @returns {null | {status: 'short'|'fine'|'long', tone: 'good'|'warn'|'info',
 *   headline: string, detail: string, schools: number, min: number, max: number}}
 */
export function lengthVerdict(schools, { stillBuilding = false } = {}) {
  const n = Number.isFinite(schools) && schools > 0 ? Math.floor(schools) : 0;
  if (n === 0) return null;
  const { min, max } = LIST_LENGTH;
  const base = { schools: n, min, max };
  if (n < min) {
    return stillBuilding
      ? { ...base, status: 'short', tone: 'info', headline: 'Keep adding',
          detail: `Most students end up applying to ${min} to ${max}. Add targets and safeties first.` }
      : { ...base, status: 'short', tone: 'warn', headline: 'Too short',
          detail: `Most students apply to ${min} to ${max}. Add targets and safeties first.` };
  }
  if (n > max) {
    return { ...base, status: 'long', tone: stillBuilding ? 'info' : 'warn', headline: 'A long list',
      detail: `Past about ${max}, the supplemental essays start to thin out. Keep the schools you’d really attend.` };
  }
  return { ...base, status: 'fine', tone: 'good', headline: 'A good length',
    detail: `Inside the ${min} to ${max} most students apply to.` };
}
